<template>
  <main class="home">
    <Header navbarType="goBack" :screenTitle="screeTitle" @clicked="goBack" />
    <TransactionDetails
      v-if="showPaymentDetails"
      :due-payment="isDuePayment"
      :transaction-info="selectedPayment"
      hide-header
    />
    <PaymentSchedule
      v-else
      :payment-schedule="paymentSchedule"
      :payoff-balance-today="payoffBalanceToday"
      :minimum-payment-amount="minimumPaymentAmount"
      @onItemClick="onItemClick"
    />
  </main>
</template>

<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  provide,
  ref,
} from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Header from "@/shared-components/consumer/Header.vue";
import PaymentSchedule from "@/apps/credit-line/components/PaymentDetails.vue";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import TransactionDetails from "@/apps/credit-line/components/Transactions/TransactionDetails.vue";
import {
  IPaymentSchedule,
  ITransaction,
  TransactionType,
} from "@/apps/credit-line/models/CreditLineDetails";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";

declare const Android: AndroidInterface;

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { summary, fetchSummary } = useCreditLineSummary();

const showPaymentDetails = ref<boolean>(false);
const selectedPayment = ref<ITransaction | null>(null);
const isDuePayment = ref<boolean>(false);
const paymentSchedule = ref<IPaymentSchedule[]>([]);

const setDuePayment = (val: boolean) => {
  isDuePayment.value = val;
};

provide("setDuePayment", setDuePayment);

const payoffBalanceToday = computed(() => Number(summary.value?.payoffBalanceToday) || 0);
const minimumPaymentAmount = computed(() => Number(summary.value?.minimumPaymentAmount) || 0);

const screeTitle = computed(() => {
  return showPaymentDetails.value
    ? t("PAYMENT_DETAILS")
    : t("UPCOMING_PAYMENTS");
});

const goBack = () => {
  if (selectedPayment.value) {
    selectedPayment.value = null;
    showPaymentDetails.value = false;
  } else {
    if (
      typeof Android?.shouldDisplayNewUi === "function" &&
      Android.shouldDisplayNewUi()
    ) {
      Android.dismissWebView();
    } else {
      router.replace({ name: "CreditLineLineDetailsView" });
    }
  }
};

const onItemClick = (item: IPaymentSchedule) => {
  try {
    selectedPayment.value = { ...item, type: TransactionType.PAYMENT_SCHEDULE };
    showPaymentDetails.value = true;
  } catch (error) {
    console.error(error);
  }
};

const getPaymentScheduleFromSummary = async (): Promise<IPaymentSchedule[]> => {
  try {
    const summaryData = await fetchSummary();
    return summaryData.paymentSchedule as IPaymentSchedule[];
  } catch (error) {
    console.error("Error populating the payment schedule", error);
    DatadogLogger.Log(
      "Error getPaymentScheduleFromSummary",
      error as Record<string, unknown> | undefined,
      LogType.ERROR
    );
    return [];
  }
};

onBeforeMount(() => {
  setLanguage(getCurrentInstance()?.proxy);
});

onMounted(async () => {
  paymentSchedule.value = await getPaymentScheduleFromSummary();

  if (isAndroid()) {
    Android.finishedLoading();
  }
});
</script>
